<template>

  <!-- ══════════════════════════════════════════
       DESKTOP HERO  (hidden on mobile ≤768px)
  ══════════════════════════════════════════ -->
  <section class="hero">
    <!-- Animated gradient blobs -->
    <div class="hero-bg" aria-hidden="true">
      <div class="blob blob-1"></div>
      <div class="blob blob-2"></div>
      <div class="blob blob-3"></div>
      <div class="blob blob-4"></div>
    </div>

    <!-- Floating math symbols -->
    <div class="math-symbols" aria-hidden="true">
      <span class="ms ms-1">∑</span>
      <span class="ms ms-2">π</span>
      <span class="ms ms-3">√</span>
      <span class="ms ms-4">∫</span>
      <span class="ms ms-5">∞</span>
      <span class="ms ms-6">θ</span>
      <span class="ms ms-7">Δ</span>
      <span class="ms ms-8">λ</span>
      <span class="ms ms-9">±</span>
      <span class="ms ms-10">≠</span>
      <span class="ms ms-11">α</span>
      <span class="ms ms-12">β</span>
      <span class="ms ms-13">γ</span>
      <span class="ms ms-14">ω</span>
    </div>

    <div class="hero-container">
      <!-- Right: Teacher info -->
      <div class="hero-info">
        <div class="hero-badge">
          <span class="badge-dot"></span>
          {{ config.badge_text || '🎓 منصة الرياضيات الأولى' }}
        </div>

        <h1 class="hero-name" v-if="auth">أهلاً، {{ userName }} 👋</h1>
        <h1 class="hero-name" v-else>{{ config.name_guest || 'أ/ طارق عبدالباري' }}</h1>

        <p class="hero-subtitle">{{ config.subtitle || 'مدرس الرياضيات للمرحلة الإعدادية والثانوية' }}</p>

        <p class="hero-desc" v-if="!auth">
          {{ config.description_guest || 'انضم إلى آلاف الطلاب الذين يتفوقون في الرياضيات مع منهج متطور، شرح مبسط، وامتحانات تفاعلية.' }}
        </p>
        <p class="hero-desc" v-else>
          {{ config.description_auth || 'أكمل رحلتك التعليمية — دروسك ونتائجك في انتظارك' }}
          <span v-if="academicYearLabel" class="level-chip">{{ academicYearLabel }}</span>
        </p>

        <!-- CTAs for guests -->
        <div class="hero-actions" v-if="!auth">
          <router-link :to="ctaPrimary.link" class="btn-hero-primary">
          {{ ctaPrimary.label }}
          </router-link>
          <a :href="config.whatsapp_url || 'https://wa.me/+201280194879'" target="_blank" class="btn-hero-wa">
            <i class="pi pi-whatsapp"></i> {{ config.whatsapp_label || 'تواصل معنا' }}
          </a>
        </div>

        <!-- CTAs for authenticated -->
        <div class="hero-actions" v-else>
          <router-link :to="ctaAuthPrimary.link" class="btn-hero-primary">{{ ctaAuthPrimary.label }}</router-link>
          <router-link :to="ctaAuthSecondary.link" class="btn-hero-secondary">{{ ctaAuthSecondary.label }}</router-link>
        </div>

        <!-- Social proof (guests only) -->
        <div class="social-proof" v-if="!auth">
          <div class="proof-avatars">
            <div class="pa pa-1">أ</div>
            <div class="pa pa-2">م</div>
            <div class="pa pa-3">س</div>
            <div class="pa pa-4">ي</div>
            <div class="pa pa-5">ف</div>
          </div>
          <span>{{ config.social_proof_text || '+2000 طالب يتعلمون الآن' }}</span>
        </div>
      </div>

      <!-- Left: Avatar + floating stat cards -->
      <div class="hero-visual">
        <div class="avatar-scene">
          <div class="avatar-glow"></div>
          <div class="avatar-ring">
            <img :src="avatarUrl" alt="أ/طارق عبدالغني عبدالباري" />
          </div>
          <div class="orbit orbit-1"><div class="orbit-dot"></div></div>
          <div class="orbit orbit-2"><div class="orbit-dot orbit-dot-2"></div></div>
        </div>

        <div v-for="(stat, i) in stats" :key="i" class="stat-card" :class="`stat-card-${i + 1}`">
          <div class="stat-card-icon">{{ stat.icon }}</div>
          <div class="stat-card-body">
            <div class="stat-card-num">{{ stat.number }}</div>
            <div class="stat-card-label">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Scroll indicator -->
    <div class="scroll-indicator">
      <div class="scroll-mouse"><div class="scroll-wheel"></div></div>
      <span>اكتشف أكثر</span>
    </div>
  </section>

  <!-- ══════════════════════════════════════════
       MOBILE APP HOME SCREEN  (hidden on desktop >768px)
       Feels like opening a premium learning app.
  ══════════════════════════════════════════ -->
  <section class="mah" dir="rtl">

    <!-- ── AUTH LAYOUT ── -->
    <template v-if="auth">

      <!-- Compact welcome (no oversized promotional hero) -->
      <div class="mah-welcome">
        <div class="mah-welcome-text">
          <span class="mah-hello">أهلاً، {{ userName }} 👋</span>
          <span class="mah-tagline">جاهز تكمل مذاكرتك؟</span>
        </div>
        <span class="mah-level-chip" v-if="academicYearLabel">{{ academicYearLabel }}</span>
      </div>

      <!-- Primary "continue" card — the most important action on the screen.
           No last-lesson/progress data exists in the API, so we do NOT fabricate
           a specific lesson or a progress bar; this is the honest primary entry
           into the student's lessons/content library. -->
      <router-link to="/contents" class="mah-continue">
        <span class="mah-continue-glow" aria-hidden="true"></span>
        <span class="mah-continue-icon">📚</span>
        <span class="mah-continue-body">
          <span class="mah-continue-eyebrow">أكمل دراستك</span>
          <span class="mah-continue-title">دروسك ومحتوى الحصص</span>
        </span>
        <span class="mah-continue-cta">متابعة <i class="pi pi-arrow-left"></i></span>
      </router-link>

      <!-- Quick actions — app shortcuts (2×2), not marketing cards -->
      <div class="mah-quick-section">
        <div class="mah-sec-title">وصول سريع</div>
        <div class="mah-quick-grid">
          <router-link to="/contents" class="mah-qcard">
            <span class="mah-qc-ic mah-ic-lessons">📚</span>
            <span class="mah-qc-label">محتوى الحصص</span>
          </router-link>
          <router-link to="/exams?type=online" class="mah-qcard">
            <span class="mah-qc-ic mah-ic-exams">📝</span>
            <span class="mah-qc-label">الامتحانات</span>
          </router-link>
          <router-link to="/exams?type=homework" class="mah-qcard">
            <span class="mah-qc-ic mah-ic-hw">🏠</span>
            <span class="mah-qc-label">الواجب المنزلي</span>
          </router-link>
          <router-link to="/my_exams" class="mah-qcard">
            <span class="mah-qc-ic mah-ic-results">📊</span>
            <span class="mah-qc-label">النتائج</span>
          </router-link>
        </div>
      </div>

    </template>

    <!-- ── GUEST LAYOUT ── -->
    <template v-else>

      <!-- Teacher hero card -->
      <div class="mah-guest-hero">
        <div class="mah-gh-blobs" aria-hidden="true">
          <div class="mah-gh-blob-1"></div>
          <div class="mah-gh-blob-2"></div>
        </div>

        <!-- Math symbols (subtle) -->
        <div class="mah-gh-math" aria-hidden="true">
          <span>∑</span><span>π</span><span>√</span><span>∫</span>
          <span>∞</span><span>θ</span><span>±</span><span>Δ</span>
        </div>

        <div class="mah-gh-inner">
          <div class="mah-gh-avatar">
            <img src="@/assets/images/favicon.png" alt="أ/طارق عبدالغني عبدالباري" />
          </div>
          <div class="mah-gh-badge">
            <span class="mah-gh-dot"></span>
            🎓 منصة الرياضيات الأولى
          </div>
          <h1 class="mah-gh-name">أ/ طارق عبدالغني عبدالباري</h1>
          <p class="mah-gh-sub">مدرس الرياضيات للمرحلة الإعدادية والثانوية</p>
          <div class="mah-gh-stats">
            <div class="mah-gh-stat"><span>2000+</span><small>طالب</small></div>
            <div class="mah-gh-stat-div"></div>
            <div class="mah-gh-stat"><span>350+</span><small>درس</small></div>
            <div class="mah-gh-stat-div"></div>
            <div class="mah-gh-stat"><span>95%</span><small>نجاح</small></div>
          </div>
        </div>
      </div>

      <!-- CTA actions -->
      <div class="mah-guest-cta">
        <router-link to="/search" class="mah-btn mah-btn-primary">
          🔍 ابحث عن حجزك الآن
        </router-link>
        <a href="https://wa.me/+201280194879" target="_blank" class="mah-btn mah-btn-wa">
          <i class="pi pi-whatsapp"></i> تواصل مع الأستاذ
        </a>
        <div class="mah-social-proof">
          <div class="mah-sp-avatars">
            <div class="mah-sp-av mah-sp-1">أ</div>
            <div class="mah-sp-av mah-sp-2">م</div>
            <div class="mah-sp-av mah-sp-3">س</div>
          </div>
          <span>+2000 طالب يتعلمون الآن</span>
        </div>
      </div>

      <!-- Features preview grid -->
      <div class="mah-features">
        <div class="mah-sec-title">لماذا منصتنا؟</div>
        <div class="mah-feat-grid">
          <div class="mah-feat-card">
            <span class="mah-fi mah-fi-purple">🎬</span>
            <div>
              <div class="mah-feat-label">دروس فيديو</div>
              <div class="mah-feat-desc">شرح واضح بجودة عالية</div>
            </div>
          </div>
          <div class="mah-feat-card">
            <span class="mah-fi mah-fi-blue">📝</span>
            <div>
              <div class="mah-feat-label">امتحانات تفاعلية</div>
              <div class="mah-feat-desc">تصحيح فوري ومراجعة</div>
            </div>
          </div>
          <div class="mah-feat-card">
            <span class="mah-fi mah-fi-green">📊</span>
            <div>
              <div class="mah-feat-label">متابعة التقدم</div>
              <div class="mah-feat-desc">تتبع أداءك ونتائجك</div>
            </div>
          </div>
          <div class="mah-feat-card">
            <span class="mah-fi mah-fi-orange">💬</span>
            <div>
              <div class="mah-feat-label">دعم مستمر</div>
              <div class="mah-feat-desc">تواصل مباشر مع الأستاذ</div>
            </div>
          </div>
        </div>
      </div>

    </template>

  </section>

</template>

<script setup>
import { computed, onMounted } from 'vue';
import { useAcademicYears } from '@/composables/useAcademicYears';
import logoAsset from '@/assets/images/logo.png';

const props = defineProps({
  auth: { type: Boolean, default: false },
  user: { type: Object, default: null },
  config: { type: Object, default: () => ({}) },
});

const { getAcademicYearName, fetchAcademicYears } = useAcademicYears();
onMounted(fetchAcademicYears);

const userName = computed(() => props.user?.name?.split(' ')[0] || '');
const academicYearLabel = computed(() => getAcademicYearName(props.user?.academic_year_id));

// teacher_avatar already comes back as an absolute URL (resolved server-side
// by HomepageSectionResource, same convention as Banner::image_link) — falls
// back to the original bundled asset when no avatar has been uploaded yet.
const avatarUrl = computed(() => props.config.teacher_avatar || logoAsset);

const ctaPrimary = computed(() => props.config.cta_primary || { label: '🔍 ابحث عن حجزك الآن', link: '/search' });
const ctaAuthPrimary = computed(() => props.config.cta_auth_primary || { label: '📚 محتوى الحصص', link: '/contents' });
const ctaAuthSecondary = computed(() => props.config.cta_auth_secondary || { label: '📝 الامتحانات', link: '/exams?type=online' });

const DEFAULT_STATS = [
  { icon: '👨‍🎓', number: '2000+', label: 'طالب متميز' },
  { icon: '📚', number: '350+', label: 'درس مسجّل' },
  { icon: '🏆', number: '95%', label: 'نسبة النجاح' },
];
const stats = computed(() => (props.config.stats?.length ? props.config.stats : DEFAULT_STATS));
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800;900&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

/* ══════════════════════════════════════════
   DESKTOP HERO  (all original styles preserved)
══════════════════════════════════════════ */

/* ── Shared buttons ── */
.btn-hero-primary, .btn-hero-secondary, .btn-hero-wa {
  display: inline-flex; align-items: center; justify-content: center; gap: .6rem;
  padding: .9rem 2rem; border-radius: 99px;
  font-weight: 700; font-size: 1rem; text-decoration: none;
  transition: all .3s cubic-bezier(.4,0,.2,1); border: none; cursor: pointer;
  font-family: 'Cairo', sans-serif;
}
.btn-hero-primary  { background: linear-gradient(135deg,#5B5FEF,#7C4DFF); color:#fff; box-shadow:0 12px 32px rgba(91,95,239,.3); }
.btn-hero-primary:hover  { transform:translateY(-3px); box-shadow:0 20px 50px rgba(91,95,239,.45); color:#fff; }
.btn-hero-secondary { background:transparent; color:#5B5FEF; border:2px solid rgba(91,95,239,.25) !important; }
.btn-hero-secondary:hover { background:rgba(91,95,239,.06); border-color:#5B5FEF !important; transform:translateY(-2px); color:#5B5FEF; }
.btn-hero-wa { background:linear-gradient(135deg,#25d366,#128C7E); color:#fff; box-shadow:0 12px 30px rgba(37,211,102,.25); }
.btn-hero-wa:hover { transform:translateY(-3px); box-shadow:0 20px 45px rgba(37,211,102,.4); color:#fff; }

/* ── Hero ── */
.hero {
  min-height: 100vh;
  background: linear-gradient(135deg,#0f0c29 0%,#302b63 45%,#24243e 100%);
  position: relative; overflow: hidden;
  display: flex; flex-direction: column; align-items: stretch;
  font-family: 'Cairo', sans-serif; direction: rtl;
}

/* Blobs */
.hero-bg { position:absolute; inset:0; pointer-events:none; }
.blob { position:absolute; border-radius:50%; filter:blur(80px); opacity:.35; animation:blobMove 8s ease-in-out infinite; }
.blob-1 { width:600px;height:600px; background:radial-gradient(circle,#5B5FEF,#7C4DFF); top:-200px; right:-100px; }
.blob-2 { width:500px;height:500px; background:radial-gradient(circle,#00C2FF,#0066ff); bottom:-150px;left:-100px; animation-delay:-3s; }
.blob-3 { width:400px;height:400px; background:radial-gradient(circle,#00D4A6,#00C2FF); top:40%;left:35%; animation-delay:-5s; }
.blob-4 { width:300px;height:300px; background:radial-gradient(circle,#7C4DFF,#a855f7); top:60%;right:20%; animation-delay:-2s; }
@keyframes blobMove { 0%,100%{transform:scale(1) translate(0,0)} 33%{transform:scale(1.1) translate(30px,-30px)} 66%{transform:scale(.9) translate(-20px,20px)} }

/* Math symbols */
.math-symbols { position:absolute;inset:0;pointer-events:none;overflow:hidden; }
.ms { position:absolute;color:rgba(255,255,255,.08);font-size:3rem;font-family:'Georgia',serif;font-weight:700;animation:mathFloat 6s ease-in-out infinite;user-select:none; }
.ms-1  { top:10%;right:8%;font-size:4rem;animation-duration:7s; }
.ms-2  { top:25%;right:25%;font-size:2.5rem;animation-delay:-1s;animation-duration:8s; }
.ms-3  { top:60%;right:15%;font-size:3.5rem;animation-delay:-2s;animation-duration:6s; }
.ms-4  { top:15%;left:12%;font-size:3rem;animation-delay:-3s;animation-duration:9s; }
.ms-5  { top:50%;left:8%;font-size:4.5rem;animation-delay:-1.5s;animation-duration:7s; }
.ms-6  { top:75%;left:20%;font-size:2.8rem;animation-delay:-4s;animation-duration:8s; }
.ms-7  { top:35%;left:40%;font-size:2rem;animation-delay:-2.5s;animation-duration:10s; }
.ms-8  { top:80%;right:35%;font-size:3rem;animation-delay:-.5s;animation-duration:7s; }
.ms-9  { top:5%;left:50%;font-size:2.5rem;animation-delay:-3.5s;animation-duration:9s; }
.ms-10 { top:90%;right:55%;font-size:2rem;animation-delay:-1s;animation-duration:8s; }
.ms-11 { top:40%;right:5%;font-size:2.5rem;animation-delay:-4.5s;animation-duration:6s; }
.ms-12 { top:65%;left:45%;font-size:3rem;animation-delay:-2s;animation-duration:11s; }
.ms-13 { top:20%;left:30%;font-size:2rem;animation-delay:-3s;animation-duration:8s; }
.ms-14 { top:85%;left:65%;font-size:3.5rem;animation-duration:7s; }
@keyframes mathFloat { 0%,100%{transform:translate(0,0) rotate(0deg);opacity:.08} 25%{transform:translate(15px,-20px) rotate(8deg);opacity:.14} 75%{transform:translate(-10px,15px) rotate(-6deg);opacity:.06} }

/* Hero layout */
.hero-container {
  max-width:1200px;margin:0 auto;width:100%;
  padding:120px 2rem 80px;
  display:grid;grid-template-columns:1fr 1fr;gap:3rem;align-items:center;
  position:relative;z-index:1;flex:1;
}
.hero-info { display:flex;flex-direction:column;gap:1.5rem; }

.hero-badge {
  display:inline-flex;align-items:center;gap:.5rem;
  background:rgba(255,255,255,.1);backdrop-filter:blur(10px);
  border:1px solid rgba(255,255,255,.2);border-radius:99px;
  padding:.4rem 1.1rem;color:rgba(255,255,255,.9);font-size:.85rem;font-weight:600;
  width:fit-content;animation:fadeSlideIn .8s ease both;
}
.badge-dot { width:8px;height:8px;border-radius:50%;background:#00D4A6;box-shadow:0 0 8px #00D4A6;animation:pulseGreen 1.5s infinite; }
@keyframes pulseGreen { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.4);opacity:.6} }

.hero-name { font-size:clamp(2.2rem,5vw,3.5rem);font-weight:900;color:#fff;line-height:1.15;letter-spacing:-.02em;animation:fadeSlideIn .8s ease .1s both; }
.hero-subtitle { font-size:1.1rem;color:rgba(255,255,255,.7);font-weight:500;animation:fadeSlideIn .8s ease .2s both; }
.hero-desc { font-size:1rem;color:rgba(255,255,255,.6);line-height:1.8;max-width:480px;animation:fadeSlideIn .8s ease .3s both; }
.level-chip { display:inline-block;background:rgba(0,212,166,.15);color:#00D4A6;border:1px solid rgba(0,212,166,.3);border-radius:99px;padding:.15rem .75rem;font-size:.82rem;font-weight:700;margin-right:.5rem; }
.hero-actions { display:flex;gap:1rem;flex-wrap:wrap;animation:fadeSlideIn .8s ease .4s both; }
.hero-actions .btn-hero-primary, .hero-actions .btn-hero-secondary, .hero-actions .btn-hero-wa { width:auto; }

.social-proof { display:flex;align-items:center;gap:.75rem;animation:fadeSlideIn .8s ease .5s both;color:rgba(255,255,255,.6);font-size:.88rem; }
.proof-avatars { display:flex; }
.pa { width:34px;height:34px;border-radius:50%;border:2px solid rgba(255,255,255,.3);display:flex;align-items:center;justify-content:center;font-size:.85rem;font-weight:700;color:#fff;margin-left:-8px; }
.pa:first-child { margin-left:0; }
.pa-1 { background:linear-gradient(135deg,#5B5FEF,#7C4DFF); }
.pa-2 { background:linear-gradient(135deg,#00C2FF,#0066ff); }
.pa-3 { background:linear-gradient(135deg,#00D4A6,#00C2FF); }
.pa-4 { background:linear-gradient(135deg,#7C4DFF,#a855f7); }
.pa-5 { background:linear-gradient(135deg,#f59e0b,#ef4444); }

/* Hero visual */
.hero-visual { position:relative;display:flex;align-items:center;justify-content:center;height:500px;animation:fadeSlideIn .8s ease .2s both; }
.avatar-scene { position:relative;display:flex;align-items:center;justify-content:center; }
.avatar-glow { position:absolute;width:280px;height:280px;border-radius:50%;background:radial-gradient(circle,rgba(91,95,239,.5) 0%,transparent 70%);animation:glowPulse 3s ease-in-out infinite; }
@keyframes glowPulse { 0%,100%{transform:scale(1);opacity:.5} 50%{transform:scale(1.2);opacity:.8} }
.avatar-ring { width:200px;height:200px;border-radius:50%;padding:5px;background:linear-gradient(135deg,#5B5FEF,#7C4DFF,#00C2FF);box-shadow:0 0 60px rgba(91,95,239,.5),0 0 120px rgba(91,95,239,.2);animation:floatY 4s ease-in-out infinite;position:relative;z-index:1;display:flex;align-items:center;justify-content:center; }
.avatar-ring img { width:100%;height:100%;border-radius:50%;border:4px solid #fff;object-fit:cover; }
.orbit { position:absolute;border-radius:50%;border:1px solid rgba(255,255,255,.15);display:flex;align-items:flex-start;justify-content:flex-end; }
.orbit-1 { width:280px;height:280px;animation:orbitSpin 8s linear infinite; }
.orbit-2 { width:350px;height:350px;animation:orbitSpin 12s linear infinite reverse; }
.orbit-dot { width:12px;height:12px;border-radius:50%;background:linear-gradient(135deg,#5B5FEF,#00C2FF);box-shadow:0 0 12px rgba(91,95,239,.8);margin:-6px;transform:translateY(6px); }
.orbit-dot-2 { background:linear-gradient(135deg,#00D4A6,#00C2FF);box-shadow:0 0 12px rgba(0,212,166,.8); }
@keyframes orbitSpin { to { transform:rotate(360deg); } }

.stat-card { position:absolute;background:rgba(255,255,255,.1);backdrop-filter:blur(20px);border:1px solid rgba(255,255,255,.2);border-radius:20px;padding:1rem 1.25rem;display:flex;align-items:center;gap:.85rem;min-width:150px;box-shadow:0 16px 40px rgba(0,0,0,.2);transition:transform .3s;cursor:default; }
.stat-card:hover { transform:scale(1.05); }
.stat-card-icon { font-size:1.8rem; }
.stat-card-num { font-size:1.4rem;font-weight:800;color:#fff;line-height:1; }
.stat-card-label { font-size:.75rem;color:rgba(255,255,255,.7);margin-top:.1rem; }
.stat-card-1 { top:5%;right:-5%;animation:floatCard1 4s ease-in-out infinite; }
.stat-card-2 { top:45%;left:-5%;animation:floatCard2 5s ease-in-out infinite; }
.stat-card-3 { bottom:8%;right:5%;animation:floatCard3 3.5s ease-in-out infinite; }
@keyframes floatCard1 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-8px,-12px)} }
@keyframes floatCard2 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(10px,8px)} }
@keyframes floatCard3 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-6px,-10px)} }

/* Scroll indicator */
.scroll-indicator { position:absolute;bottom:2rem;left:50%;transform:translateX(-50%);display:flex;flex-direction:column;align-items:center;gap:.5rem;color:rgba(255,255,255,.5);font-size:.8rem;z-index:1;animation:fadeIn 1s ease 1s both; }
.scroll-mouse { width:24px;height:38px;border:2px solid rgba(255,255,255,.4);border-radius:12px;display:flex;justify-content:center;padding-top:5px; }
.scroll-wheel { width:4px;height:8px;background:rgba(255,255,255,.6);border-radius:2px;animation:scrollWheel 1.5s ease infinite; }
@keyframes scrollWheel { 0%{opacity:1;transform:translateY(0)} 100%{opacity:0;transform:translateY(12px)} }

/* Animations */
@keyframes floatY { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
@keyframes fadeSlideIn { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
@keyframes fadeIn { from{opacity:0} to{opacity:1} }

/* Desktop responsive (900px tablet) */
@media(max-width:900px) {
  .hero-container { grid-template-columns:1fr;padding:100px 1.5rem 60px;gap:2rem; }
  .hero-visual { height:320px;order:-1; }
  .avatar-ring { width:150px;height:150px; }
  .orbit-1 { width:210px;height:210px; }
  .orbit-2 { width:260px;height:260px; }
  .stat-card-1 { top:0;right:0; }
  .stat-card-2 { display:none; }
  .stat-card-3 { bottom:0;right:0; }
  .hero-name { font-size:2.2rem; }
  .hero-actions { flex-direction:column; }
  .hero-actions .btn-hero-primary, .hero-actions .btn-hero-wa { width:100%; }
}
@media(max-width:600px) { .hero-container { padding:80px 1rem 50px; } }

/* ── Show/hide control ── */
.mah { display: none; }

@media (max-width: 768px) {
  .hero { display: none; }
  .mah  { display: flex; }
}

/* ══════════════════════════════════════════
   MOBILE APP HOME SCREEN  (.mah)
   Everything below is mobile-only.
══════════════════════════════════════════ */
.mah {
  flex-direction: column;
  background: #F5F7FB;
  font-family: 'Cairo', 'Almarai', sans-serif;
  direction: rtl;
  overflow-x: hidden;
  animation: mahFadeIn .35s ease both;
}
@keyframes mahFadeIn { from { opacity: 0; } to { opacity: 1; } }

/* ── Compact welcome (auth) — light, no oversized hero ── */
.mah-welcome {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .6rem;
  padding: .85rem 1rem .35rem;
  min-width: 0;
}
.mah-welcome-text {
  display: flex;
  flex-direction: column;
  gap: .05rem;
  min-width: 0;
}
.mah-hello {
  font-size: 1.15rem;
  font-weight: 800;
  color: #111827;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mah-tagline {
  font-size: .8rem;
  color: #6B7280;
  font-weight: 500;
}
.mah-level-chip {
  background: rgba(91,95,239,.1);
  border: 1px solid rgba(91,95,239,.22);
  color: #5B5FEF;
  border-radius: 99px;
  padding: .26rem .8rem;
  font-size: .72rem;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
}

/* ── Primary "continue" card (the one strong gradient accent) ── */
.mah-continue {
  position: relative;
  overflow: hidden;
  margin: .5rem 1rem 0;
  padding: .95rem 1rem;
  display: flex;
  align-items: center;
  gap: .85rem;
  text-decoration: none;
  border-radius: 20px;
  background: linear-gradient(135deg, #5B5FEF 0%, #7C4DFF 100%);
  box-shadow: 0 12px 28px rgba(91,95,239,.28);
  -webkit-tap-highlight-color: transparent;
  transition: transform .16s ease;
}
.mah-continue:active { transform: scale(.98); }
.mah-continue-glow {
  position: absolute;
  top: -40%; inset-inline-end: -12%;
  width: 150px; height: 150px;
  background: radial-gradient(circle, rgba(255,255,255,.28), transparent 70%);
  pointer-events: none;
}
.mah-continue-icon {
  flex-shrink: 0;
  width: 46px; height: 46px;
  border-radius: 14px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.5rem;
  background: rgba(255,255,255,.18);
  border: 1px solid rgba(255,255,255,.25);
}
.mah-continue-body { display: flex; flex-direction: column; gap: .12rem; min-width: 0; flex: 1; }
.mah-continue-eyebrow { font-size: .72rem; font-weight: 700; color: rgba(255,255,255,.82); }
.mah-continue-title {
  font-size: 1rem; font-weight: 800; color: #fff; line-height: 1.25;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.mah-continue-cta {
  flex-shrink: 0;
  display: inline-flex; align-items: center; gap: .3rem;
  background: #fff; color: #5B5FEF;
  font-size: .78rem; font-weight: 800;
  padding: .4rem .75rem; border-radius: 99px;
}
.mah-continue-cta i { font-size: .7rem; }

/* ── Quick actions (auth) — compact light app shortcuts ── */
.mah-quick-section {
  padding: 1rem 1rem .5rem;
}
.mah-sec-title {
  font-size: .72rem;
  font-weight: 800;
  color: #9CA3AF;
  letter-spacing: .04em;
  margin-bottom: .55rem;
  padding: 0 .15rem;
}
.mah-quick-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: .6rem;
}
.mah-qcard {
  min-height: 80px;
  border-radius: 16px;
  padding: .7rem .8rem;
  display: flex;
  align-items: center;
  gap: .65rem;
  text-decoration: none;
  background: #fff;
  border: 1px solid rgba(17,24,39,.06);
  box-shadow: 0 4px 14px rgba(17,24,39,.05);
  transition: transform .16s ease, box-shadow .16s ease;
  -webkit-tap-highlight-color: transparent;
}
.mah-qcard:active { transform: scale(.96); }
.mah-qc-ic {
  flex-shrink: 0;
  width: 42px; height: 42px;
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.3rem; color: #fff;
}
/* Small gradient surfaces (intelligent brand use), not full-card gradients */
.mah-ic-lessons { background: linear-gradient(145deg, #5B5FEF, #7C4DFF); }
.mah-ic-exams   { background: linear-gradient(145deg, #00C2FF, #0066ff); }
.mah-ic-hw      { background: linear-gradient(145deg, #00D4A6, #00B4D8); }
.mah-ic-results { background: linear-gradient(145deg, #f59e0b, #ef4444); }
.mah-qc-label { font-size: .86rem; font-weight: 800; color: #1f2937; line-height: 1.25; }

/* ── Guest hero card ── */
.mah-guest-hero {
  background: linear-gradient(160deg, #5B5FEF 0%, #6C55F5 55%, #7C4DFF 100%);
  position: relative;
  overflow: hidden;
  padding: 1.6rem 1.25rem 1.5rem;
  border-radius: 0 0 28px 28px;
  box-shadow: 0 10px 30px rgba(91,95,239,.22);
}
.mah-gh-blobs { position: absolute; inset: 0; pointer-events: none; }
.mah-gh-blob-1 {
  position: absolute; width: 280px; height: 280px;
  background: radial-gradient(circle, rgba(255,255,255,.22), transparent);
  top: -80px; right: -60px; border-radius: 50%; filter: blur(40px);
}
.mah-gh-blob-2 {
  position: absolute; width: 220px; height: 220px;
  background: radial-gradient(circle, rgba(0,212,166,.3), transparent);
  bottom: -60px; left: -40px; border-radius: 50%; filter: blur(40px);
}
.mah-gh-math {
  position: absolute; inset: 0; overflow: hidden; pointer-events: none;
  display: flex; flex-wrap: wrap;
}
.mah-gh-math span {
  position: absolute;
  font-family: 'Georgia', serif;
  font-size: 2rem;
  color: rgba(255,255,255,.06);
  font-weight: 700;
  user-select: none;
}
.mah-gh-math span:nth-child(1) { top: 8%;  right: 6%; font-size: 2.5rem; }
.mah-gh-math span:nth-child(2) { top: 20%; left: 8%;  font-size: 1.8rem; }
.mah-gh-math span:nth-child(3) { top: 55%; right: 12%; font-size: 2rem; }
.mah-gh-math span:nth-child(4) { top: 65%; left: 5%;  font-size: 2.8rem; }
.mah-gh-math span:nth-child(5) { top: 10%; left: 40%; font-size: 3rem; }
.mah-gh-math span:nth-child(6) { bottom: 15%; right: 35%; font-size: 1.6rem; }
.mah-gh-math span:nth-child(7) { bottom: 8%;  left: 30%; font-size: 2.2rem; }
.mah-gh-math span:nth-child(8) { top: 40%; right: 30%; font-size: 1.5rem; }

.mah-gh-inner {
  position: relative; z-index: 1;
  display: flex; flex-direction: column;
  align-items: center; text-align: center;
  gap: .6rem;
}
.mah-gh-avatar {
  width: clamp(60px, 18vw, 72px);
  height: clamp(60px, 18vw, 72px);
  border-radius: 50%;
  padding: 3px;
  background: linear-gradient(135deg, rgba(255,255,255,.9), rgba(255,255,255,.35));
  box-shadow: 0 12px 32px rgba(0,0,0,.22);
  display: flex; align-items: center; justify-content: center;
  animation: mahFloat 3.5s ease-in-out infinite;
}
.mah-gh-avatar img {
  width: 100%; height: 100%;
  border-radius: 50%;
  border: 3px solid #fff;
  object-fit: cover;
}
@keyframes mahFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-7px)} }

.mah-gh-badge {
  display: inline-flex; align-items: center; gap: .4rem;
  background: rgba(255,255,255,.1); border: 1px solid rgba(255,255,255,.2);
  border-radius: 99px; padding: .28rem .9rem;
  font-size: .75rem; font-weight: 600; color: rgba(255,255,255,.9);
}
.mah-gh-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: #00D4A6; box-shadow: 0 0 6px #00D4A6;
  animation: ghDotPulse 1.5s infinite;
}
@keyframes ghDotPulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.5);opacity:.6} }

.mah-gh-name {
  font-size: 1.7rem; font-weight: 900; color: #fff;
  line-height: 1.3; letter-spacing: -.01em;
}
.mah-gh-sub {
  font-size: .78rem; color: rgba(255,255,255,.8);
  line-height: 1.5;
  background: rgba(255,255,255,.12); padding: .3rem 1rem;
  border-radius: 99px; backdrop-filter: blur(8px);
  border: 1px solid rgba(255,255,255,.18);
}
.mah-gh-stats {
  display: flex; align-items: center; gap: .85rem;
  padding: .6rem 1.35rem;
  background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.2);
  border-radius: 16px; backdrop-filter: blur(12px);
  margin-top: .2rem;
}
.mah-gh-stat {
  display: flex; flex-direction: column; align-items: center; gap: .05rem;
}
.mah-gh-stat span {
  font-size: 1.15rem; font-weight: 900; color: #fff; line-height: 1;
}
.mah-gh-stat small {
  font-size: .65rem; color: rgba(255,255,255,.6); font-weight: 500;
}
.mah-gh-stat-div {
  width: 1px; height: 30px; background: rgba(255,255,255,.18);
}

/* ── Guest CTA section ── */
.mah-guest-cta {
  padding: 1.25rem 1rem 1rem;
  background: transparent;
  display: flex; flex-direction: column; gap: .7rem;
}
.mah-btn {
  display: flex; align-items: center; justify-content: center; gap: .55rem;
  padding: .9rem 1.5rem;
  border-radius: 99px;
  font-weight: 700; font-size: .97rem;
  text-decoration: none; color: #fff;
  font-family: 'Cairo', 'Almarai', sans-serif;
  width: 100%; border: none; cursor: pointer;
  transition: opacity .15s ease, transform .15s ease;
  -webkit-tap-highlight-color: transparent;
}
.mah-btn:active { transform: scale(0.97); opacity: .92; color: #fff; }
.mah-btn-primary { background: linear-gradient(135deg, #5B5FEF, #7C4DFF); box-shadow: 0 12px 32px rgba(91,95,239,.3); }
.mah-btn-wa { background: linear-gradient(135deg, #25d366, #128C7E); box-shadow: 0 12px 30px rgba(37,211,102,.25); }

.mah-social-proof {
  display: flex; align-items: center; justify-content: center; gap: .55rem;
  font-size: .8rem; color: #6B7280; padding-top: .1rem;
}
.mah-sp-avatars { display: flex; }
.mah-sp-av {
  width: 26px; height: 26px;
  border-radius: 50%; border: 2px solid #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: .72rem; font-weight: 700; color: #fff;
  margin-left: -5px;
}
.mah-sp-av:first-child { margin-left: 0; }
.mah-sp-1 { background: linear-gradient(135deg, #5B5FEF, #7C4DFF); }
.mah-sp-2 { background: linear-gradient(135deg, #00C2FF, #0066ff); }
.mah-sp-3 { background: linear-gradient(135deg, #00D4A6, #00C2FF); }

/* ── Features grid (guest) ── */
.mah-features {
  padding: 1.15rem 1rem 1rem;
  background: #F5F7FB;
}
.mah-feat-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: .6rem;
}
.mah-feat-card {
  background: #fff;
  border-radius: 16px;
  padding: .95rem .9rem;
  display: flex;
  align-items: flex-start;
  gap: .65rem;
  border: 1px solid rgba(91,95,239,.06);
  box-shadow: 0 2px 10px rgba(0,0,0,.05);
  transition: transform .18s ease;
  -webkit-tap-highlight-color: transparent;
}
.mah-feat-card:active { transform: scale(0.97); }
.mah-fi {
  width: 38px; height: 38px;
  border-radius: 11px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
}
.mah-fi-purple { background: linear-gradient(135deg, rgba(91,95,239,.1),  rgba(124,77,255,.1)); }
.mah-fi-blue   { background: linear-gradient(135deg, rgba(0,194,255,.1),   rgba(0,102,255,.1)); }
.mah-fi-green  { background: linear-gradient(135deg, rgba(0,212,166,.1),   rgba(0,194,255,.1)); }
.mah-fi-orange { background: linear-gradient(135deg, rgba(245,158,11,.1),  rgba(239,68,68,.1)); }
.mah-feat-label { font-size: .83rem; font-weight: 700; color: #1A1A1A; margin-bottom: .15rem; }
.mah-feat-desc  { font-size: .7rem; color: #6B7280; line-height: 1.5; }
</style>
